
class ProductCalculator extends HTMLElement {
  constructor() {
    super();

    this.state = {
      unit: (this.dataset.unit || 'm'), // 'm' | 'ft' (UI display)
      trenched: null, // true | false
      inputs:  { distance: null, height: null, length: null },
      inputs_m:{ distance: null, height: null, length: null },
      required:{ width_m: null, length_m: 1, chosen_width_m: null },
      currency: this.dataset.currency,
      products: [], // [{ el, id, title, priceEl, variants:[{id,width_m,price_cents,available}] }]
      selection: { productEl: null, productId: null, variantId: null, unitPriceCents: 0, quantity_m: 1 } // fake-quantity (meters)
    };

    this.configuratorEl = this.querySelector('.product-calculator-configurator-wrapper');
    this.resultsEl = this.querySelector('.product-calculator-results-container');
    this.resultsListEl = this.querySelector('.product-calculator-results-products-container');
    this.showButtonEl = this.querySelector('[data-action="show-results"], .product-calculator-show-results');
    this.editButtonEl = this.querySelector('[data-action="edit"], .product-calculator-results-edit');
    this.atcButtonEl = this.querySelector('[data-action="add-to-cart"], .product-calculator-results-atc');
    this.toggleUnitEl = this.querySelector('[data-action="toggle-unit"], .product-calculator-configurator-toggle');
    this.answersContainerEl = this.querySelector('.product-calculator-question-container') || this;
    this.inputDistanceEl = this.querySelector('[data-field="distance"]') || this.nthInput(0);
    this.inputLengthEl = this.querySelector('[data-field="length"]') || this.nthInput(1);
    this.inputHeightEl = this.querySelector('[data-field="height"]') || this.nthInput(2);
    this.bindTrenchedEl = this.querySelector('[data-bind="trenched"]');
    this.bindDistanceEl = this.querySelector('[data-bind="distance"]');
    this.bindHeightEl = this.querySelector('[data-bind="height"]');
    this.bindLengthEl = this.querySelector('[data-bind="length"]');
  }

  connectedCallback() {
    this.parseProducts();
    this.bindEvents();
    this.ensureAtcTotalSpan();
    this.showConfigurator();
    this.updateAtcButtonState();
  }

  bindEvents() {
    // Unit toggle
    this.toggleUnitEl?.addEventListener('click', () => {
      this.state.unit = this.state.unit === 'm' ? 'ft' : 'm';
      this.dataset.unit = this.state.unit;
      this.updateMeasureLabels();
      this.reflectInputsForUnit();
      this.reflectResultsForUnit();
      this.updateAtcButtonTotal();
    });

    // Yes/No answers
    this.answersContainerEl?.addEventListener('click', (e) => {
      const button = e.target.closest('[data-answer]');
      if (!button) return;
      const answer = (button.dataset.answer || '').toLowerCase();
      if (answer !== 'yes' && answer !== 'no') return;
      this.state.trenched = (answer === 'yes');
      this.markAnswer(answer);
    });

    // Configurator inputs
    ['distance','height','length'].forEach((key) => {
      const input = key === 'distance' ? this.inputDistanceEl : key === 'height' ? this.inputHeightEl : this.inputLengthEl;
      if (!input) return;
      input.addEventListener('input', () => this.handleConfiguratorInput(key, input.value));
      input.addEventListener('blur',  () => this.handleConfiguratorInput(key, input.value, true));
    });

    // Show results / Edit / ATC
    this.showButtonEl?.addEventListener('click', (e) => this.onShowResults(e));
    this.editButtonEl?.addEventListener('click', () => this.showConfigurator());
    this.atcButtonEl?.addEventListener('click', () => this.onAddToCart());

    // Radio change per product
    this.resultsEl?.addEventListener('change', (e) => {
      const radio = e.target.closest('.product-calculator-results-product-input');
      if (!radio) return;
      const card = radio.closest('.product-calculator-results-product-container');
      if (!card) return;

      const allRadios = this.resultsEl.querySelectorAll('.product-calculator-results-product-input[type="radio"]');
      allRadios.forEach(r => { if (r !== radio) r.checked = false; });

      if (!card.dataset.variantId) return;

      this.state.selection.productEl = card;
      this.state.selection.productId = radio.value || card.dataset.productId;
      this.state.selection.variantId = card.dataset.variantId;
      this.state.selection.unitPriceCents = parseInt(card.dataset.unitPriceCents || '0', 10) || 0;

      this.recomputeAllTotals();
      this.updateAtcButtonState();
      this.updateAtcButtonTotal();
    });

    // length editor (results)
    const onResultsLength = (e, finalize) => {
      const input = e.target.closest('[data-role="result-length"]') || e.target.closest('.product-calculator-results-configurator-wrapper input[type="number"]');
      if (!input) return;
      this.handleResultLengthInput(input, input.value, finalize);
    };
    this.resultsEl?.addEventListener('input', (e) => onResultsLength(e, false));
    this.resultsEl?.addEventListener('blur',  (e) => onResultsLength(e, true), true);
  }

  parseProducts() {
    const cards = [...this.querySelectorAll('.product-calculator-results-product-container')];
    this.state.products = cards.map(card => {
      const productId = card.querySelector('.product-calculator-results-product-input')?.value || card.id || null;
      const title = card.querySelector('.product-calculator-results-product-title')?.textContent?.trim() || '';
      const priceEl = card.querySelector('.product-calculator-results-product-price');
      const mapRoot = card.querySelector('[data-role="variant-map"]') || card.querySelector('.hidden');

      const variants = [];
      if (mapRoot) {
        mapRoot.querySelectorAll('span[data-value]').forEach(sp => {
          const raw = (sp.getAttribute('data-value') || '').toString().replace(',', '.').trim();
          const width = parseFloat(raw);
          const id = sp.getAttribute('data-id');
          const priceCents = parseInt(sp.getAttribute('data-price') || '0', 10);
          const availableAttr = sp.getAttribute('data-available');
          const available = (availableAttr == null) ? true : (availableAttr === 'true');

          if (!isFinite(width)) return; // ignore variants without metafield width
          variants.push({ id, width_m: width, price_cents: priceCents, available });
        });
      }

      return { el: card, id: productId, title, priceEl, variants };
    });
  }

  handleConfiguratorInput(key, rawValue, finalize = false) {
    const parsedValue = this.parseNumber(rawValue);
    this.state.inputs[key] = isFinite(parsedValue) ? parsedValue : null;

    const meters = this.toMeters(parsedValue, this.state.unit);
    this.state.inputs_m[key] = isFinite(meters) ? Math.max(0, meters) : null;
  }

  onShowResults(e) {
    e.preventDefault();
    const state = this.state;
    if (state.trenched == null || !isFinite(state.inputs_m.distance) || !isFinite(state.inputs_m.height) || !isFinite(state.inputs_m.length)) {
      console.warn('Not valid measurements');
      return;
    }

    // Required width (m)
    const widthRequired = state.inputs_m.distance + (state.trenched ? 1.5 : 0.5);
    state.required.width_m = widthRequired;

    // Required length (ceil to integer meters)
    const rawRequiredLength = state.inputs_m.height + state.inputs_m.height + state.inputs_m.length;
    const requiredLength = Math.max(1, Math.ceil(rawRequiredLength));
    state.required.length_m = requiredLength;

    // Initial fake-quantity equals required length
    state.selection.quantity_m = requiredLength;

    // Determine target width (smallest available >= required)
    const allWidths = [];
    this.state.products.forEach(p => p.variants.forEach(v => { if (v.available) allWidths.push(v.width_m); }));
    const uniqueSortedWidths = [...new Set(allWidths)].sort((a,b)=>a-b);
    const targetWidth = uniqueSortedWidths.find(w => w + 1e-9 >= widthRequired);
    state.required.chosen_width_m = isFinite(targetWidth) ? targetWidth : null;

    // Show/hide cards by matching exact chosen width
    const visibleCards = [];
    this.state.products.forEach(product => {
      product.el.style.display = 'none';
      product.el.dataset.variantId = '';
      product.el.dataset.unitPriceCents = '';
      product.el.dataset.widthChoiceM = '';

      if (!isFinite(targetWidth)) return;
      const variant = product.variants.find(v => v.available && Math.abs(v.width_m - targetWidth) < 1e-6);
      if (!variant) return;

      product.el.style.display = '';
      product.el.dataset.variantId = variant.id;
      product.el.dataset.unitPriceCents = String(variant.price_cents);
      product.el.dataset.widthChoiceM = String(targetWidth);

      if (product.priceEl) product.priceEl.textContent = this.formatMoney(variant.price_cents);

      const detailsEl = product.el.querySelector('.product-calculator-results-configurator-details');
      if (detailsEl) {
        const widthDisplay = this.formatValue(this.fromMeters(targetWidth, this.state.unit));
        const unit = this.unitLabel();
        detailsEl.textContent = `Width: ${widthDisplay} (${unit}) x Length:`;
      }

      visibleCards.push(product.el);
    });

    this.renderResultsDetails();
    this.broadcastResultLengthInputs(state.selection.quantity_m);
    this.showResults();

    // Preselect first visible card
    const firstRadio = this.resultsEl?.querySelector('.product-calculator-results-product-container:not([style*="display: none"]) .product-calculator-results-product-input');
    if (firstRadio) {
      firstRadio.checked = true;
      firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.state.selection = { productEl: null, productId: null, variantId: null, unitPriceCents: 0, quantity_m: state.selection.quantity_m };
    }

    this.ensureNoMatchBanner(visibleCards.length === 0);
    this.recomputeAllTotals();
    this.updateAtcButtonState();
    this.updateAtcButtonTotal();
  }

  handleResultLengthInput(inputEl, rawValue, finalize) {
    const parsedValue = this.parseNumber(rawValue);
    const meters = this.toMeters(parsedValue, this.state.unit);
    const quantityMeters = Math.max(1, Math.ceil(isFinite(meters) ? meters : 0));
    this.state.selection.quantity_m = quantityMeters;
    this.broadcastResultLengthInputs(quantityMeters);
    this.recomputeAllTotals();
    this.updateAtcButtonTotal();
  }

  async onAddToCart() {
    const selection = this.state.selection;
    if (!selection.variantId || !isFinite(selection.quantity_m) || selection.quantity_m < 1) {
      console.warn('Not valid measurements.');
      return;
    }
    const card = selection.productEl;
    if (!card || card.style.display === 'none') {
      console.warn('This variant is unavailable.');
      return;
    }

    const unit = this.unitLabel();
    const formatInCurrentUnit = (m) => `${this.formatValue(this.fromMeters(m, this.state.unit))} ${unit}`;

    const variantId = Number(selection.variantId);
    const lengthPerRoll = selection.quantity_m;
    const properties = {
      'Length (m)': String(lengthPerRoll),
      'Quantity': 1,
      _source: 'Product Calculator',
      _trenched: this.state.trenched ? 'Yes' : 'No',
      _unit: this.state.unit,
      _distance_input_display: formatInCurrentUnit(this.state.inputs_m.distance),
      _height_input_display: formatInCurrentUnit(this.state.inputs_m.height),
      _length_input_display: formatInCurrentUnit(this.state.inputs_m.length),
      _distance_input_m: this.formatValue(this.state.inputs_m.distance),
      _height_input_m: this.formatValue(this.state.inputs_m.height),
      _length_input_m: this.formatValue(this.state.inputs_m.length),
      _computed_width_m: this.formatValue(this.state.required.width_m),
      _chosen_width_m: this.state.required.chosen_width_m != null ? this.formatValue(this.state.required.chosen_width_m) : '',
      _computed_length_m: String(this.state.required.length_m),
      _selected_length_m: String(lengthPerRoll)
    };

    try {
      // Check for an existing cart item with the same variant and length so we can
      // increment Quantity correctly rather than letting Shopify merge with Quantity still at 1.
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const existingIndex = cart.items.findIndex(it =>
        it.variant_id === variantId &&
        it.properties &&
        String(it.properties['Length (m)']) === String(lengthPerRoll)
      );

      let res;
      if (existingIndex !== -1) {
        const existing = cart.items[existingIndex];
        const existingRolls = parseInt(existing.properties['Quantity'] || '1', 10);
        const newTotalRolls = existingRolls + 1;
        const newTotalMeters = Math.max(1, Math.round(lengthPerRoll * newTotalRolls));
        res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            line: existingIndex + 1,
            quantity: newTotalMeters,
            properties: { ...properties, 'Quantity': String(newTotalRolls) }
          }),
          credentials: 'same-origin'
        });
      } else {
        res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: Math.max(1, Math.floor(lengthPerRoll)),
            properties
          }),
          credentials: 'same-origin'
        });
      }

      const responseData = await res.json();

      if (res.status === 422) {
        const errorMessage = responseData.description || responseData.errors || responseData.message;

        if (errorMessage) {
          await this.refreshCartUISections();

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (!cartDrawer.classList.contains('active')) {
              cartDrawer.open();
            }
            this.displayCartItemError(responseData, variantId);
          }
        }
        return;
      }

      if (!res.ok) {
        console.warn('This variant is unavailable');
        return;
      }

      await this.refreshCartUISections();

      if (PUB_SUB_EVENTS?.cartUpdate && typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-calculator' });
      }
    } catch (e) {
      console.warn('This variant is unavailable', e);
    }
  }

  async refreshCartUISections() {
    const cartUrl = (window.routes && routes.cart_url) ? routes.cart_url : '/cart';
    const resp = await fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`, { credentials: 'same-origin' });
    const sections = await resp.json();

    if (sections['cart-drawer']) {
      const drawerFrag = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const src = drawerFrag.querySelector('.drawer__inner');
      const drawerTarget = document.querySelector('#shopify-section-cart-drawer cart-drawer #CartDrawer.cart-drawer .drawer__inner');
      if (src && drawerTarget) {
        drawerTarget.innerHTML = src.innerHTML;
      }
    }

    if (sections['cart-icon-bubble']) {
      const bubbleRoot = document.getElementById('cart-icon-bubble');
      if (bubbleRoot) {
        const bubbleFrag = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const src = bubbleFrag.querySelector('.shopify-section') || bubbleFrag.body;
        const target = bubbleRoot.querySelector('.shopify-section') || bubbleRoot;
        if (src && target) {
          target.innerHTML = src.innerHTML;
        }
      }
    }

    try {
      const cartJson = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const isEmpty = (cartJson?.item_count || 0) === 0;
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
      if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
    } catch {}
  }

  displayCartItemError(response, variantId) {
    if (!response || !variantId) return;
    
    setTimeout(() => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;
      
      const cartItems = cartDrawer.querySelectorAll('[id^="CartDrawer-Item-"]');
      let targetLine = null;
      
      cartItems.forEach((itemRow) => {
        const quantityInput = itemRow.querySelector(`input[data-quantity-variant-id="${variantId}"]`);
        if (quantityInput) {
          const lineMatch = itemRow.id?.match(/CartDrawer-Item-(\d+)/);
          if (lineMatch) {
            targetLine = parseInt(lineMatch[1], 10);
          }
        }
      });
      
      if (targetLine) {
        const errorMessage = response.description || response.errors || response.message;
        if (errorMessage) {
          const errorElement = document.getElementById(`CartDrawer-LineItemError-${targetLine}`);
          if (errorElement) {
            const errorText = errorElement.querySelector('.cart-item__error-text');
            if (errorText) {
              errorText.textContent = errorMessage;
              errorElement.style.display = '';
            }
          }
        }
      }
    }, 100);
  }

  renderResultsDetails() {
    const unit = this.unitLabel();
    const state = this.state;

    const writeSpanValue = (rootEl, valueText) => {
      const span = rootEl?.querySelector('span');
      if (span) span.textContent = valueText;
    };

    writeSpanValue(this.bindTrenchedEl, this.state.trenched ? 'Yes' : 'No');
    writeSpanValue(this.bindDistanceEl, `${this.formatValue(this.fromMeters(state.inputs_m.distance, this.state.unit))} (${unit})`);
    writeSpanValue(this.bindHeightEl, `${this.formatValue(this.fromMeters(state.inputs_m.height, this.state.unit))} (${unit})`);
    writeSpanValue(this.bindLengthEl, `${this.formatValue(this.fromMeters(state.inputs_m.length, this.state.unit))} (${unit})`);
  }

  recomputeAllTotals() {
    const quantityMeters = Math.max(1, Math.floor(this.state.selection.quantity_m));
    const cards = [...this.resultsEl?.querySelectorAll('.product-calculator-results-product-container') || []].filter(c => c.style.display !== 'none');

    cards.forEach(card => {
      const priceCents = parseInt(card.dataset.unitPriceCents || '0', 10) || 0;
      const totalCents = priceCents * quantityMeters;
      const totalEl = card.querySelector('.product-calculator-results-configurator-total span');
      if (totalEl) totalEl.textContent = this.formatMoney(totalCents);
    });

    this.updateAtcButtonTotal();
  }

  ensureNoMatchBanner(show) {
    const list = this.resultsListEl;
    if (!list) return;
    let banner = list.querySelector('.product-calculator-no-match');
    if (show) {
      if (!banner) {
        banner = document.createElement('div');
        banner.className = 'product-calculator-no-match';
        banner.textContent = 'No matching products';
        list.appendChild(banner);
      }
    } else {
      banner?.remove();
    }
  }

  writeLengthInput(input, quantity_m) {
    input.step = '0.01';
    input.dataset.quantityM = String(quantity_m);
    if (this.state.unit === 'm') {
      input.value = String(quantity_m);
    } else {
      const feet = this.fromMeters(quantity_m, 'ft');
      input.value = this.formatValue(feet, 2);
    }
  }

  broadcastResultLengthInputs(quantity_m) {
    const cards = [...this.resultsEl?.querySelectorAll('.product-calculator-results-product-container') || []].filter(c => c.style.display !== 'none');
    cards.forEach(card => {
      const input = card.querySelector('[data-role="result-length"]') || card.querySelector('.product-calculator-results-configurator-wrapper input[type="number"]');
      if (input) this.writeLengthInput(input, quantity_m);
    });
  }

  updateMeasureLabels() {
    const label = this.unitLabel();
    this.querySelectorAll('.measure').forEach(sp => sp.textContent = label);
  }

  reflectInputsForUnit() {
    ['distance','height','length'].forEach((key) => {
      const el = key === 'distance' ? this.inputDistanceEl : key === 'height' ? this.inputHeightEl : this.inputLengthEl;
      if (!el) return;
      const meters = this.state.inputs_m[key];
      if (!isFinite(meters)) return;
      const valueInUnit = this.fromMeters(meters, this.state.unit);
      el.value = this.formatValue(valueInUnit);
    });
  }

  reflectResultsForUnit() {
    if (this.resultsEl && this.resultsEl.style.display !== 'none') {
      this.renderResultsDetails();
      const cards = [...this.resultsEl.querySelectorAll('.product-calculator-results-product-container') || []]
        .filter(c => c.style.display !== 'none');
      cards.forEach(card => {
        const detailsEl = card.querySelector('.product-calculator-results-configurator-details');
        const widthChoiceM = parseFloat(card.dataset.widthChoiceM || '');
        if (detailsEl && isFinite(widthChoiceM)) {
          const widthDisplay = this.formatValue(this.fromMeters(widthChoiceM, this.state.unit));
          const unit = this.unitLabel();
          detailsEl.textContent = `Width: ${widthDisplay} (${unit}) x Length:`;
        }
      });

      // sync length inputs
      this.broadcastResultLengthInputs(this.state.selection.quantity_m);
    }
  }

  showConfigurator() {
    if (this.configuratorEl) this.configuratorEl.style.display = '';
    if (this.resultsEl) this.resultsEl.style.display = 'none';
    this.updateAtcButtonState();
  }

  showResults() {
    if (this.configuratorEl) this.configuratorEl.style.display = 'none';
    if (this.resultsEl) this.resultsEl.style.display = '';
    this.updateAtcButtonState();
  }

  markAnswer(which) {
    const all = [...this.querySelectorAll('.product-calculator-answer')];
    all.forEach(el => el.classList.toggle('is-active', (el.dataset.answer || '').toLowerCase() === which));
  }

  ensureAtcTotalSpan() {
    const button = this.atcButtonEl;
    if (!button) return;
    let totalSpan = button.querySelector('[data-role="atc-total"]');
    if (!totalSpan) {
      totalSpan = document.createElement('span');
      totalSpan.setAttribute('data-role', 'atc-total');
      button.appendChild(totalSpan);
    }
    this.updateAtcButtonTotal();
  }

  updateAtcButtonState() {
    const button = this.atcButtonEl;
    if (!button) return;
    const disabled = !this.state.selection.variantId || (this.resultsEl && this.resultsEl.style.display === 'none');
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    button.classList.toggle('is-disabled', disabled);
  }

  updateAtcButtonTotal() {
    const button = this.atcButtonEl;
    if (!button) return;
    const span = button.querySelector('[data-role="atc-total"]');
    if (!span) return;

    const priceCents = this.state.selection.unitPriceCents || 0;
    const qtyMeters = Math.max(1, Math.floor(this.state.selection.quantity_m || 1));
    const totalCents = priceCents * qtyMeters;
    span.textContent = totalCents > 0 ? this.formatMoney(totalCents) : '';
  }

  parseNumber(value) {
    if (value == null) return NaN;
    return parseFloat(String(value).replace(',', '.').trim());
  }

  toMeters(val, unit) {
    if (!isFinite(val)) return NaN;
    return unit === 'ft' ? (val * 0.3048) : val;
  }

  fromMeters(meters, unit) {
    if (!isFinite(meters)) return NaN;
    return unit === 'ft' ? (meters / 0.3048) : meters;
  }

  formatValue(num, digits = 2) {
    if (!isFinite(num)) return '';
    const s = num.toFixed(digits);
    return digits ? s.replace(/\.?0+$/, '') : s;
  }

  formatMoney(cents) {
    const amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.state.currency
      }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }

  unitLabel() {
    return this.state.unit === 'ft' ? 'ft' : 'm';
  }

  nthInput(n) {
    const list = this.querySelectorAll('.product-calculator-configurator-field-wrapper input');
    return list?.[n] || null;
  }
}

customElements.define('product-calculator', ProductCalculator);
